import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from "firebase/firestore"
import { db } from "../firebase/config"
import type { Enquiry } from "../types/enquiry"

// Helper function to convert Firebase Timestamp to ISO string
const convertTimestamp = <T>(data: T): T => {
  if (data instanceof Timestamp) {
    return data.toDate().toISOString() as T
  }
  if (Array.isArray(data)) {
    return data.map(convertTimestamp) as T
  }
  if (data && typeof data === 'object' && data !== null) {
    return Object.fromEntries(
      Object.entries(data as Record<string, unknown>).map(([key, value]) => [key, convertTimestamp(value)])
    ) as T
  }
  return data
}

// Enquiries Service
export const enquiriesService = {
  // Get all enquiries
  async getEnquiries(): Promise<Enquiry[]> {
    try {
      const enquiriesRef = collection(db, "enquiries")
      const q = query(enquiriesRef, orderBy("createdAt", "desc"))
      const snapshot = await getDocs(q)
      return snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...convertTimestamp(data),
        } as Enquiry
      })
    } catch (error) {
      console.error("Error getting enquiries:", error)
      throw error
    }
  },

  // Get enquiry by ID
  async getEnquiry(id: string): Promise<Enquiry | null> {
    try {
      const enquiryRef = doc(db, "enquiries", id)
      const enquiryDoc = await getDoc(enquiryRef)

      if (enquiryDoc.exists()) {
        const data = enquiryDoc.data()
        return {
          id: enquiryDoc.id,
          ...convertTimestamp(data),
        } as Enquiry
      }
      return null
    } catch (error) {
      console.error("Error getting enquiry:", error)
      throw error
    }
  },

  // Add new enquiry
  async addEnquiry(enquiry: Omit<Enquiry, "id" | "status" | "createdAt" | "updatedAt">): Promise<string> {
    try {
      const enquiriesRef = collection(db, "enquiries")
      const docRef = await addDoc(enquiriesRef, {
        ...enquiry,
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return docRef.id
    } catch (error) {
      console.error("Error adding enquiry:", error)
      throw error
    }
  },

  // Update enquiry
  async updateEnquiry(id: string, enquiry: Partial<Enquiry>): Promise<void> {
    try {
      const enquiryRef = doc(db, "enquiries", id)
      await updateDoc(enquiryRef, {
        ...enquiry,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating enquiry:", error)
      throw error
    }
  },

  // Update enquiry status
  async updateEnquiryStatus(enquiryId: string, status: Enquiry["status"], notes?: string): Promise<void> {
    try {
      const enquiryRef = doc(db, "enquiries", enquiryId)
      const updateData: Partial<Enquiry> = {
        status,
        updatedAt: serverTimestamp(),
      }

      if (status === "resolved") {
        updateData.resolvedAt = new Date().toISOString()
      }

      if (notes) {
        updateData.notes = notes
      }

      await updateDoc(enquiryRef, updateData)
    } catch (error) {
      console.error("Error updating enquiry status:", error)
      throw error
    }
  },

  // Assign enquiry to user
  async assignEnquiry(enquiryId: string, assignedTo: string): Promise<void> {
    try {
      const enquiryRef = doc(db, "enquiries", enquiryId)
      await updateDoc(enquiryRef, {
        assignedTo,
        status: "in-progress",
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error assigning enquiry:", error)
      throw error
    }
  },

  // Delete enquiry
  async deleteEnquiry(id: string): Promise<void> {
    try {
      const enquiryRef = doc(db, "enquiries", id)
      await deleteDoc(enquiryRef)
    } catch (error) {
      console.error("Error deleting enquiry:", error)
      throw error
    }
  },

  // Get enquiries by status
  async getEnquiriesByStatus(status: Enquiry["status"]): Promise<Enquiry[]> {
    try {
      const enquiriesRef = collection(db, "enquiries")
      const q = query(enquiriesRef, where("status", "==", status), orderBy("createdAt", "desc"))
      const snapshot = await getDocs(q)
      return snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...convertTimestamp(data),
        } as Enquiry
      })
    } catch (error) {
      console.error("Error getting enquiries by status:", error)
      throw error
    }
  },

  // Search enquiries
  async searchEnquiries(searchTerm: string): Promise<Enquiry[]> {
    try {
      const enquiries = await this.getEnquiries()
      return enquiries.filter(
        (enquiry) =>
          enquiry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          enquiry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          enquiry.phone.includes(searchTerm) ||
          enquiry.enquiry.toLowerCase().includes(searchTerm.toLowerCase())
      )
    } catch (error) {
      console.error("Error searching enquiries:", error)
      throw error
    }
  },

  // Get enquiry statistics
  async getEnquiryStats() {
    try {
      const enquiries = await this.getEnquiries()

      return {
        total: enquiries.length,
        pending: enquiries.filter((e) => e.status === "pending").length,
        inProgress: enquiries.filter((e) => e.status === "in-progress").length,
        resolved: enquiries.filter((e) => e.status === "resolved").length,
        today: enquiries.filter((e) => {
          const today = new Date().toDateString()
          const enquiryDate = new Date(e.createdAt).toDateString()
          return today === enquiryDate
        }).length,
      }
    } catch (error) {
      console.error("Error getting enquiry stats:", error)
      throw error
    }
  },

  // Real-time listener for enquiries
  onEnquiriesChange(callback: (enquiries: Enquiry[]) => void) {
    return onSnapshot(
      query(collection(db, "enquiries"), orderBy("createdAt", "desc")),
      (snapshot) => {
        const enquiries = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            ...convertTimestamp(data),
          } as Enquiry
        })
        callback(enquiries)
      },
      (error) => {
        console.error("Error listening to enquiries:", error)
      },
    )
  },

  // Real-time listener for enquiry stats
  onEnquiryStatsChange(callback: (stats: any) => void) {
    return this.onEnquiriesChange((enquiries) => {
      const stats = {
        total: enquiries.length,
        pending: enquiries.filter((e) => e.status === "pending").length,
        inProgress: enquiries.filter((e) => e.status === "in-progress").length,
        resolved: enquiries.filter((e) => e.status === "resolved").length,
      }
      callback(stats)
    })
  },
}
